import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  Modal,
  Pressable,
  StyleSheet,
  Platform,
  ActivityIndicator,
} from 'react-native';

const GOLD = '#d4af37';

/**
 * Password (+ optional OTP) modal for clearing 2FA enrollment.
 */
export default function TwoFactorResetModal({
  visible,
  onClose,
  title = 'Reset authenticator',
  description,
  requireOtp = false,
  submitLabel = 'Reset',
  onSubmit,
  busy = false,
}) {
  const [password, setPassword] = useState('');
  const [otp, setOtp] = useState('');

  useEffect(() => {
    if (!visible) {
      setPassword('');
      setOtp('');
    }
  }, [visible]);

  const handleSubmit = async () => {
    if (!password.trim()) return;
    if (requireOtp && !otp.trim()) return;
    await onSubmit({ password: password.trim(), otp: otp.trim() || undefined });
  };

  return (
    <Modal visible={visible} transparent animationType="fade" onRequestClose={onClose}>
      <View style={styles.overlay}>
        <Pressable style={styles.backdrop} onPress={busy ? undefined : onClose} />
        <View style={styles.card} nativeID="two-factor-reset-modal">
          <Text style={styles.title}>{title}</Text>
          {description ? <Text style={styles.body}>{description}</Text> : null}
          <Text style={styles.label}>Password</Text>
          <TextInput
            style={styles.input}
            value={password}
            onChangeText={setPassword}
            secureTextEntry
            placeholder="Your account password"
            placeholderTextColor="#888"
            editable={!busy}
            autoCapitalize="none"
          />
          {requireOtp ? (
            <>
              <Text style={[styles.label, { marginTop: 12 }]}>Authenticator code</Text>
              <TextInput
                style={styles.input}
                value={otp}
                onChangeText={setOtp}
                placeholder="000000"
                placeholderTextColor="#888"
                keyboardType="number-pad"
                maxLength={8}
                editable={!busy}
              />
            </>
          ) : null}
          <View style={styles.actions} nativeID="two-factor-reset-modal-actions">
            <View
              style={styles.cancelBtnShell}
              nativeID="two-factor-reset-modal-cancel"
              {...(Platform.OS === 'web'
                ? { dataSet: { twoFactorResetCancel: 'true' } }
                : {})}
            >
              <TouchableOpacity
                style={styles.cancelBtnInner}
                onPress={onClose}
                disabled={busy}
                activeOpacity={0.85}
              >
                <Text style={styles.cancelText}>Cancel</Text>
              </TouchableOpacity>
            </View>
            <View
              style={[styles.submitBtnShell, busy && styles.submitDisabled]}
              nativeID="two-factor-reset-modal-submit"
              {...(Platform.OS === 'web'
                ? { dataSet: { twoFactorResetSubmit: 'true' } }
                : {})}
            >
              <TouchableOpacity
                style={styles.submitBtnInner}
                onPress={() => void handleSubmit()}
                disabled={busy}
                activeOpacity={0.85}
              >
                {busy ? (
                  <ActivityIndicator color="#000" />
                ) : (
                  <Text style={styles.submitText}>{submitLabel}</Text>
                )}
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </View>
    </Modal>
  );
}

const styles = StyleSheet.create({
  overlay: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 16,
  },
  backdrop: {
    ...StyleSheet.absoluteFillObject,
    backgroundColor: 'rgba(0, 0, 0, 0.85)',
  },
  card: {
    width: '100%',
    maxWidth: 420,
    backgroundColor: '#1a1a1a',
    borderRadius: 12,
    borderWidth: 2,
    borderColor: GOLD,
    padding: 18,
    zIndex: 1,
  },
  title: {
    fontSize: 18,
    fontWeight: '700',
    color: GOLD,
    textAlign: 'center',
    marginBottom: 10,
  },
  body: {
    fontSize: 13,
    lineHeight: 19,
    color: '#E8E8E8',
    marginBottom: 14,
  },
  label: {
    fontSize: 14,
    color: GOLD,
    marginBottom: 6,
    fontWeight: '600',
  },
  input: {
    backgroundColor: '#2a2a2a',
    borderRadius: 8,
    paddingHorizontal: 14,
    paddingVertical: 12,
    fontSize: 16,
    color: '#FFF',
    borderWidth: 1,
    borderColor: '#3a3a3a',
    ...(Platform.OS === 'web' ? { outlineStyle: 'none' } : {}),
  },
  actions: {
    flexDirection: 'row',
    justifyContent: 'flex-end',
    gap: 12,
    marginTop: 18,
    backgroundColor: 'transparent',
  },
  cancelBtnShell: {
    backgroundColor: '#999',
    borderRadius: 8,
    minWidth: 100,
    overflow: 'hidden',
  },
  cancelBtnInner: {
    backgroundColor: 'transparent',
    paddingVertical: 12,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  cancelText: {
    color: '#FFFFFF',
    fontSize: 16,
    fontWeight: '600',
  },
  submitBtnShell: {
    backgroundColor: GOLD,
    borderRadius: 8,
    minWidth: 100,
    overflow: 'hidden',
  },
  submitBtnInner: {
    backgroundColor: 'transparent',
    paddingVertical: 12,
    paddingHorizontal: 20,
    alignItems: 'center',
    justifyContent: 'center',
  },
  submitDisabled: {
    opacity: 0.7,
  },
  submitText: {
    color: '#000',
    fontSize: 16,
    fontWeight: '700',
  },
});
